import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { listSpaces, getRecentContents, renameContent, deleteContent, createSpace, updateSpace, deleteSpace } from '../lib/api'
import { cn, formatDate, contentTypeConfig } from '../lib/utils'
import { useAuth } from '../contexts/AuthContext'
import {
  FolderOpen,
  Plus,
  FileText,
  Globe,
  Image,
  Mic,
  BookOpen,
  File,
  ChevronRight,
  Sparkles,
  Clock,
  Loader2,
  Pencil,
  Trash2,
  Layers,
  ArrowRight,
  Search,
  Command,
  Brain,
  Zap,
  Library,
  PenLine,
  Upload,
  TrendingUp,
  Star,
  MoreHorizontal,
} from 'lucide-react'
import { ImportContentModal } from '../components/ImportContentModal'
import { RenameDialog, ConfirmDialog, CreateSpaceDialog } from '../components/ContextMenu'

const iconMap = {
  note: FileText,
  bookmark: Globe,
  image: Image,
  pdf: FileText,
  docx: FileText,
  doc: FileText,
  xlsx: FileText,
  xls: FileText,
  pptx: FileText,
  ppt: FileText,
  txt: FileText,
  epub: BookOpen,
  transcript: Mic,
  unknown: File,
}

function ContentIcon({ type, className }) {
  const Icon = iconMap[type] || File
  const config = contentTypeConfig[type] || contentTypeConfig.unknown
  return (
    <div
      className={cn("p-2.5 rounded-xl", className)}
      style={{ backgroundColor: `${config.color}15` }}
    >
      <Icon className="w-4 h-4" style={{ color: config.color }} />
    </div>
  )
}

function SpaceCard({ space, onClick, onRename, onDelete }) {
  return (
    <div className="group relative card-interactive p-4 cursor-pointer" onClick={onClick}>
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/10 group-hover:bg-primary/15 transition-colors">
          <FolderOpen className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium truncate group-hover:text-primary transition-colors">
            {space.name}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {space.contents?.length || 0} items
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
      </div>

      {/* Action buttons */}
      <div className="absolute right-2 top-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => { e.stopPropagation(); onRename?.() }}
          className="p-1.5 rounded-lg hover:bg-accent"
          title="Rename"
        >
          <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete?.() }}
          className="p-1.5 rounded-lg hover:bg-destructive/10"
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5 text-destructive" />
        </button>
      </div>
    </div>
  )
}

function ContentCard({ content, onClick, onRename, onDelete, compact = false }) {
  const config = contentTypeConfig[content.type] || contentTypeConfig.unknown

  if (compact) {
    return (
      <button
        onClick={onClick}
        className="group flex items-center gap-3 p-3 rounded-xl hover:bg-accent/50 transition-colors w-full text-left"
      >
        <ContentIcon type={content.type} />
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium truncate group-hover:text-primary transition-colors">
            {content.title}
          </h4>
          <p className="text-xs text-muted-foreground">
            {formatDate(content.updated_at || content.updatedAt || content.timestamp)}
          </p>
        </div>
      </button>
    )
  }

  return (
    <div className="group relative card-interactive p-4 cursor-pointer" onClick={onClick}>
      <div className="flex items-start gap-3">
        <ContentIcon type={content.type} />
        <div className="flex-1 min-w-0">
          <h3 className="font-medium truncate group-hover:text-primary transition-colors">
            {content.title}
          </h3>
          <div className="flex items-center gap-2 mt-1.5">
            <span
              className="text-xs px-2 py-0.5 rounded-full font-medium"
              style={{ backgroundColor: `${config.color}15`, color: config.color }}
            >
              {config.label}
            </span>
            <span className="text-xs text-muted-foreground">
              {formatDate(content.updated_at || content.updatedAt || content.timestamp)}
            </span>
          </div>
          {content.description && (
            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
              {content.description}
            </p>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="absolute right-2 top-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => { e.stopPropagation(); onRename?.() }}
          className="p-1.5 rounded-lg hover:bg-accent"
          title="Rename"
        >
          <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete?.() }}
          className="p-1.5 rounded-lg hover:bg-destructive/10"
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5 text-destructive" />
        </button>
      </div>
    </div>
  )
}

// Stats display component
function StatCard({ icon: Icon, label, value, trend, color = 'primary' }) {
  const colors = {
    primary: 'bg-primary/10 text-primary',
    green: 'bg-emerald-500/10 text-emerald-500',
    blue: 'bg-blue-500/10 text-blue-500',
    purple: 'bg-purple-500/10 text-purple-500',
  }

  return (
    <div className="flex items-center gap-3 p-4 rounded-xl bg-muted/50">
      <div className={cn("p-2.5 rounded-xl", colors[color])}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
      {trend && (
        <div className="ml-auto flex items-center gap-1 text-emerald-500 text-xs font-medium">
          <TrendingUp className="w-3 h-3" />
          {trend}
        </div>
      )}
    </div>
  )
}

export default function HomePage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [spaces, setSpaces] = useState([])
  const [contents, setContents] = useState([])
  const [recentContents, setRecentContents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showImportModal, setShowImportModal] = useState(false)
  const [defaultSpaceId, setDefaultSpaceId] = useState(null)

  // Dialog states
  const [renameDialog, setRenameDialog] = useState({ open: false, type: null, id: null, name: '' })
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null, name: '' })
  const [createSpaceDialog, setCreateSpaceDialog] = useState({ open: false, parentId: null })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      const [spacesRes, recentRes] = await Promise.all([
        listSpaces(),
        getRecentContents().catch(() => ({ contents: [] })),
      ])
      setSpaces(Array.isArray(spacesRes?.spaces) ? spacesRes.spaces : [])
      setContents(Array.isArray(spacesRes?.contents) ? spacesRes.contents : [])
      if (spacesRes?.parentId) {
        setDefaultSpaceId(spacesRes.parentId.toString())
      }
      const entries = recentRes?.entries || recentRes?.contents || recentRes
      setRecentContents(Array.isArray(entries) ? entries : [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Handlers
  const handleRenameContent = async (newName) => {
    if (!renameDialog.id) return
    await renameContent(renameDialog.id, newName)
    await loadData()
  }

  const handleDeleteContent = async () => {
    if (!deleteDialog.id) return
    await deleteContent(deleteDialog.id)
    await loadData()
  }

  const handleCreateSpace = async (name, parentId) => {
    await createSpace(name, parentId || defaultSpaceId)
    await loadData()
  }

  const handleRenameSpace = async (newName) => {
    if (!renameDialog.id) return
    await updateSpace(renameDialog.id, { name: newName })
    await loadData()
  }

  const handleDeleteSpace = async () => {
    if (!deleteDialog.id) return
    await deleteSpace(deleteDialog.id)
    await loadData()
  }

  // Get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 18) return 'Good afternoon'
    return 'Good evening'
  }

  const firstName = user?.displayName?.split(' ')[0] || 'there'
  const totalItems = spaces.length + contents.length + recentContents.length

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-2xl bg-primary/20 animate-pulse" />
            <Loader2 className="w-8 h-8 text-primary animate-spin absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-muted-foreground">Loading your knowledge base...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Hero Section */}
      <div className="mb-8 animate-fade-in">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold mb-2">
              {getGreeting()}, <span className="gradient-text">{firstName}</span>
            </h1>
            <p className="text-muted-foreground text-lg">
              Your personal knowledge hub awaits. What will you discover today?
            </p>
          </div>

          {/* Search hint */}
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('openGlobalSearch'))}
            className="hidden lg:flex items-center gap-3 px-4 py-2.5 rounded-xl border border-border bg-card hover:bg-accent/50 transition-colors group"
          >
            <Search className="w-4 h-4 text-muted-foreground" />
            <span className="text-muted-foreground">Search your knowledge...</span>
            <kbd className="flex items-center gap-1 px-2 py-1 rounded-lg bg-muted text-xs text-muted-foreground group-hover:bg-background transition-colors">
              <Command className="w-3 h-3" />K
            </kbd>
          </button>
        </div>

        {error && (
          <div className="p-4 rounded-xl bg-destructive/10 text-destructive border border-destructive/20 mb-6">
            {error}
          </div>
        )}
      </div>

      {/* Bento Grid Layout */}
      <div className="bento-grid">
        {/* Quick Actions - Large Card */}
        <div className="bento-span-2 card-interactive p-6 flex flex-col animate-fade-in" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              Quick Actions
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1">
            {[
              { icon: Sparkles, label: 'Ask AI', desc: 'Chat with content', color: '#6366f1', path: '/chat' },
              { icon: PenLine, label: 'New Note', desc: 'Write markdown', color: '#FF6B6B', action: 'note' },
              { icon: Globe, label: 'Save URL', desc: 'Import webpage', color: '#4ECDC4', action: 'url' },
              { icon: Upload, label: 'Upload', desc: 'Add files', color: '#9C27B0', action: 'file' },
            ].map((item) => (
              <button
                key={item.label}
                onClick={() => item.path ? navigate(item.path) : setShowImportModal(true)}
                className="group flex flex-col items-center justify-center gap-2 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-all hover:-translate-y-0.5"
              >
                <div
                  className="p-3 rounded-xl transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${item.color}15` }}
                >
                  <item.icon className="w-5 h-5" style={{ color: item.color }} />
                </div>
                <div className="text-center">
                  <span className="text-sm font-medium block">{item.label}</span>
                  <span className="text-xs text-muted-foreground hidden sm:block">{item.desc}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* AI Features Card */}
        <div className="bento-span-2 relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-primary/20 via-primary/10 to-purple-500/10 border border-primary/20 animate-fade-in" style={{ animationDelay: '0.15s' }}>
          <div className="relative z-10">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 rounded-xl bg-primary/20">
                <Brain className="w-6 h-6 text-primary" />
              </div>
              <span className="badge badge-primary">AI Powered</span>
            </div>
            <h3 className="text-xl font-bold mb-2">Your Knowledge, Supercharged</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Ask questions, get summaries, and discover insights across all your content with AI.
            </p>
            <div className="flex flex-wrap gap-2">
              <Link to="/chat" className="btn-primary text-sm py-2">
                <Sparkles className="w-4 h-4" />
                Start Chatting
              </Link>
              <Link to="/search" className="btn-ghost text-sm py-2">
                <Search className="w-4 h-4" />
                Semantic Search
              </Link>
            </div>
          </div>

          {/* Decorative elements */}
          <div className="absolute -right-10 -bottom-10 w-40 h-40 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute right-20 top-0 w-20 h-20 rounded-full bg-purple-500/10 blur-2xl" />
        </div>

        {/* Workspace Card */}
        <div className="bento-span-2 bento-row-2 card-interactive p-6 flex flex-col animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-lg flex items-center gap-2">
              <Layers className="w-5 h-5 text-primary" />
              Workspace
            </h2>
            <Link
              to="/kb"
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              Open
              <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <StatCard icon={FolderOpen} label="Spaces" value={spaces.length} color="primary" />
            <StatCard icon={FileText} label="Items" value={contents.length + recentContents.length} color="blue" />
          </div>

          {/* Spaces List */}
          <div className="flex-1 overflow-hidden">
            {spaces.length === 0 && contents.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-4">
                <div className="p-4 rounded-2xl bg-muted/50 mb-3">
                  <FolderOpen className="w-8 h-8 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground mb-2">No spaces yet</p>
                <button
                  onClick={() => setCreateSpaceDialog({ open: true, parentId: defaultSpaceId })}
                  className="text-sm text-primary hover:underline"
                >
                  Create your first space
                </button>
              </div>
            ) : (
              <div className="space-y-2 overflow-y-auto max-h-[300px] pr-2">
                {spaces.slice(0, 4).map((space) => (
                  <SpaceCard
                    key={`space-${space.id}`}
                    space={space}
                    onClick={() => navigate(`/spaces/${space.id}`)}
                    onRename={() => setRenameDialog({ open: true, type: 'space', id: space.id, name: space.name })}
                    onDelete={() => setDeleteDialog({ open: true, type: 'space', id: space.id, name: space.name })}
                  />
                ))}
                {contents.slice(0, 3).map((content) => (
                  <ContentCard
                    key={`content-${content.content_id || content.id}`}
                    content={content}
                    compact
                    onClick={() => navigate(`/content/${content.content_id || content.id}`)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bento-span-2 bento-row-2 card-interactive p-6 flex flex-col animate-fade-in" style={{ animationDelay: '0.25s' }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Recent Activity
            </h2>
            {recentContents.length > 0 && (
              <span className="text-xs text-muted-foreground">
                {recentContents.length} items
              </span>
            )}
          </div>

          <div className="flex-1 overflow-hidden">
            {recentContents.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-4">
                <div className="p-4 rounded-2xl bg-muted/50 mb-3">
                  <FileText className="w-8 h-8 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground mb-2">No recent activity</p>
                <button
                  onClick={() => setShowImportModal(true)}
                  className="text-sm text-primary hover:underline"
                >
                  Add your first content
                </button>
              </div>
            ) : (
              <div className="space-y-2 overflow-y-auto max-h-[400px] pr-2">
                {recentContents.slice(0, 8).map((content) => (
                  <ContentCard
                    key={content.contentId || content.content_id || content.id}
                    content={content}
                    compact
                    onClick={() => navigate(`/content/${content.contentId || content.content_id || content.id}`)}
                    onRename={() => setRenameDialog({ open: true, type: 'content', id: content.contentId || content.content_id || content.id, name: content.title })}
                    onDelete={() => setDeleteDialog({ open: true, type: 'content', id: content.contentId || content.content_id || content.id, name: content.title })}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="card-interactive p-5 flex flex-col items-center justify-center text-center cursor-pointer group animate-fade-in"
          style={{ animationDelay: '0.3s' }}
          onClick={() => navigate('/kb')}
        >
          <div className="p-3 rounded-xl bg-blue-500/10 mb-3 group-hover:scale-110 transition-transform">
            <Library className="w-6 h-6 text-blue-500" />
          </div>
          <h3 className="font-medium mb-1">Knowledge Library</h3>
          <p className="text-xs text-muted-foreground">Organize everything</p>
        </div>

        <div className="card-interactive p-5 flex flex-col items-center justify-center text-center cursor-pointer group animate-fade-in"
          style={{ animationDelay: '0.35s' }}
          onClick={() => navigate('/chat')}
        >
          <div className="p-3 rounded-xl bg-purple-500/10 mb-3 group-hover:scale-110 transition-transform">
            <Sparkles className="w-6 h-6 text-purple-500" />
          </div>
          <h3 className="font-medium mb-1">Chat with AI</h3>
          <p className="text-xs text-muted-foreground">Ask anything</p>
        </div>
      </div>

      {/* Import Content Modal */}
      <ImportContentModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={loadData}
        defaultSpaceId={defaultSpaceId}
      />

      {/* Dialogs */}
      <RenameDialog
        isOpen={renameDialog.open}
        onClose={() => setRenameDialog({ open: false, type: null, id: null, name: '' })}
        onSubmit={renameDialog.type === 'content' ? handleRenameContent : handleRenameSpace}
        initialValue={renameDialog.name}
        title={renameDialog.type === 'content' ? 'Rename Content' : 'Rename Space'}
      />

      <ConfirmDialog
        isOpen={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, type: null, id: null, name: '' })}
        onConfirm={deleteDialog.type === 'content' ? handleDeleteContent : handleDeleteSpace}
        title={`Delete ${deleteDialog.type === 'content' ? 'Content' : 'Space'}`}
        message={`Are you sure you want to delete "${deleteDialog.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
      />

      <CreateSpaceDialog
        isOpen={createSpaceDialog.open}
        onClose={() => setCreateSpaceDialog({ open: false, parentId: null })}
        onSubmit={handleCreateSpace}
        parentId={createSpaceDialog.parentId}
      />
    </div>
  )
}
